import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';

import { Stepper } from '@/features/orderNewCards/components/Stepper';
import type { CloseInfo, TrackPagesOnOrderNewCardsModal } from '@/features/orderNewCards/types';
import { trackOrderAbandon, trackOrderEntryPoint } from '@/features/orderNewCards/utils/analytics';
import { DialogType, useModal } from '@/hooks/useModal';

export const OrderNewCards = (): ReactNode => {
    const { openDialog, closeDialog } = useModal();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const isOpen = searchParams.get('modal') === 'orderCards';
    const sourceParam = searchParams.get('modalSource');
    const source: TrackPagesOnOrderNewCardsModal = isValidModalSource(sourceParam) ? sourceParam : 'direct_link';

    const [currentStepInfo, setCurrentStepInfo] = useState<CloseInfo>({
        currentStep: 0,
        stepName: 'charging_cards',
        orderStatus: 'pending',
    });

    const handleStepUpdate = useCallback((closeInfo: CloseInfo) => {
        setCurrentStepInfo(closeInfo);
    }, []);

    const handleClose = useCallback(() => {
        if (currentStepInfo.orderStatus === 'pending') {
            trackOrderAbandon(currentStepInfo.currentStep, currentStepInfo.stepName, source);
        }
        navigate('?', { replace: true });
    }, [source, currentStepInfo, navigate]);

    useEffect(() => {
        if (isOpen) {
            openDialog({
                type: DialogType.SIMPLE,
                show: true,
                onClose: handleClose,
                title: <FormattedMessage id='chargingCards.newOrder.title' />,
                body: <Stepper source={source} onStepChange={handleStepUpdate} />,
                bsSize: 'lg',
                useOverflow: true,
            });
            trackOrderEntryPoint(source);
        } else {
            closeDialog();
        }
    }, [isOpen, handleStepUpdate, handleClose, openDialog, closeDialog, source]);

    return;
};

const isValidModalSource = (value: string | null): value is TrackPagesOnOrderNewCardsModal =>
    value === 'overview_page' || value === 'admin_cards_page';
